/*
  bb-entry-gauge.js
  Boosted Brat — signature entry gauge (brief section 9)

  WHY A CUSTOM ELEMENT (read before editing)
  ------------------------------------------
  This logic used to live in an inline IIFE <script> inside the block markup.
  The theme editor re-renders a block through Shopify's Section Rendering API,
  and inline <script> tags injected that way DO NOT execute — so the gauge went
  dead the moment a merchant edited its section. A custom element instead
  auto-upgrades the instant the browser parses the tag, no matter how the markup
  arrived (first paint, SRA re-render, or app injection). Same behavior on the
  storefront, plus it now works in the editor.

  All configuration is read from the host element:
    - data-redline / data-entry-property / data-multiplier-property / data-animate
    - the inline <script type="application/json" data-bb-seed> server seed
    - data-msg-* strings (moved out of Liquid because this asset is not
      Liquid-evaluated; the .liquid interpolates them onto the element instead)

  The entry-resolution adapter (resolveLineEntries) is the ONLY place that knows
  where an entry number comes from: line property -> product metafield ->
  multiplier x price. Everything downstream is source-agnostic.
*/

class BBEntryGauge extends HTMLElement {
  connectedCallback() {
    if (this._bbInit) return;
    this._bbInit = true;

    var root = this;
    var self = this;

    var START = 135;      // degrees, lower-left
    var SWEEP = 270;      // total travel, ends lower-right
    var CX = 150, CY = 150, R = 108;
    var REDLINE_BAND = 0.15; // top 15% of the sweep is the redline zone

    var svg      = root.querySelector('[data-bb-svg]');
    var trackEl  = root.querySelector('[data-bb-track]');
    var redEl    = root.querySelector('[data-bb-redline]');
    var fillEl   = root.querySelector('[data-bb-fill]');
    var needleEl = root.querySelector('[data-bb-needle]');
    var valueEl  = root.querySelector('[data-bb-value]');
    var capEl    = root.querySelector('[data-bb-caption]');
    var liveEl   = root.querySelector('[data-bb-live]');
    var seedEl   = root.querySelector('[data-bb-seed]');

    var redline  = Math.max(1, parseInt(root.dataset.redline, 10) || 25000);
    var entryProp = (root.dataset.entryProperty || '').trim();
    var multProp  = (root.dataset.multiplierProperty || '').trim();
    var wantsAnim = root.dataset.animate !== 'false';

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var animate = wantsAnim && !reduced;

    // Message strings, moved out of Liquid onto the element as data-attributes.
    var emptyMsg      = root.dataset.msgEmpty || (capEl ? capEl.textContent : '');
    var activeMsg     = root.dataset.msgActive || '';
    var redlineMsg    = root.dataset.msgRedline || '';
    var unresolvedMsg = root.dataset.msgUnresolved || '';

    var cache = {};   // variantId -> { entries, multiplier }
    var shown = 0;

    // ---- geometry -----------------------------------------------------------
    function pt(deg, r) {
      var rad = deg * Math.PI / 180;
      return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
    }

    function arc(fromDeg, toDeg, r) {
      var a = pt(fromDeg, r), b = pt(toDeg, r);
      var large = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0;
      return 'M ' + a.x.toFixed(2) + ' ' + a.y.toFixed(2) +
             ' A ' + r + ' ' + r + ' 0 ' + large + ' 1 ' + b.x.toFixed(2) + ' ' + b.y.toFixed(2);
    }

    function drawStatic() {
      trackEl.setAttribute('d', arc(START, START + SWEEP, R));
      redEl.setAttribute('d', arc(START + SWEEP * (1 - REDLINE_BAND), START + SWEEP, R));
      fillEl.setAttribute('d', arc(START, START + SWEEP, R));

      var len = fillEl.getTotalLength();
      fillEl.style.strokeDasharray = len;
      fillEl.style.strokeDashoffset = len;
      fillEl._len = len;

      var g = root.querySelector('[data-bb-ticks]');
      var ns = 'http://www.w3.org/2000/svg';
      for (var i = 0; i <= 10; i++) {
        var isMajor = i % 5 === 0;
        var deg = START + SWEEP * (i / 10);
        var outer = pt(deg, R - 12);
        var inner = pt(deg, isMajor ? R - 26 : R - 20);
        var line = document.createElementNS(ns, 'line');
        line.setAttribute('x1', outer.x.toFixed(2));
        line.setAttribute('y1', outer.y.toFixed(2));
        line.setAttribute('x2', inner.x.toFixed(2));
        line.setAttribute('y2', inner.y.toFixed(2));
        line.setAttribute('class', isMajor ? 'bb-gauge__tick bb-gauge__tick--major' : 'bb-gauge__tick');
        g.appendChild(line);

        if (isMajor) {
          var lp = pt(deg, R - 42);
          var t = document.createElementNS(ns, 'text');
          t.setAttribute('x', lp.x.toFixed(2));
          t.setAttribute('y', lp.y.toFixed(2));
          t.setAttribute('class', 'bb-gauge__tick-label');
          t.textContent = shortNum(Math.round(redline * (i / 10)));
          g.appendChild(t);
        }
      }
    }

    function shortNum(n) {
      if (n >= 1000) {
        var k = n / 1000;
        return (k >= 10 ? Math.round(k) : Math.round(k * 10) / 10) + 'K';
      }
      return String(n);
    }

    function fmt(n) { return n.toLocaleString('en-US'); }

    // ---- entry resolution adapter ------------------------------------------
    // The ONLY place that knows where an entry number comes from.
    function readProp(props, key) {
      if (!props || !key) return null;
      if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];
      var lower = key.toLowerCase();
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k) && k.toLowerCase() === lower) return props[k];
      }
      return null;
    }

    function toNum(v) {
      if (v === null || v === undefined || v === '') return null;
      var n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
      return isFinite(n) ? n : null;
    }

    // Returns { entries: Number, resolved: Boolean }
    function resolveLineEntries(line) {
      var props = line.properties || {};
      var qty = line.qty || line.quantity || 1;
      var cached = cache[line.id] || {};

      // 1. line item property written by the entry app (per-unit)
      var direct = toNum(readProp(props, entryProp));
      if (direct !== null) return { entries: Math.round(direct * qty), resolved: true };

      // 2. mirrored product metafield, seeded server-side
      var mf = toNum(line.entries !== undefined ? line.entries : cached.entries);
      if (mf !== null) return { entries: Math.round(mf * qty), resolved: true };

      // 3. tier multiplier x line price (price is in cents)
      var mult = toNum(readProp(props, multProp));
      if (mult === null) mult = toNum(line.multiplier !== undefined ? line.multiplier : cached.multiplier);
      var linePrice = toNum(line.line_price);
      if (linePrice === null) {
        var unit = toNum(line.unit_price);
        if (unit !== null) linePrice = unit * qty;
      }
      if (mult !== null && linePrice !== null) {
        return { entries: Math.round((linePrice / 100) * mult), resolved: true };
      }

      return { entries: 0, resolved: false };
    }

    function totalFor(lines) {
      var total = 0, unresolved = 0;
      for (var i = 0; i < lines.length; i++) {
        var r = resolveLineEntries(lines[i]);
        total += r.entries;
        if (!r.resolved) unresolved++;
      }
      return { total: total, unresolved: unresolved, count: lines.length };
    }

    // ---- render -------------------------------------------------------------
    // Needle art is drawn pointing at 12 o'clock, which is 270deg in the
    // 0deg-is-east/clockwise convention used by pt(). Rotating it to the
    // gauge angle therefore means (START + SWEEP * ratio) - 270.
    function setNeedle(ratio) {
      var deg = START + SWEEP * ratio - 270;
      needleEl.style.transform = 'rotate(' + deg.toFixed(2) + 'deg)';
    }

    function countTo(target) {
      if (self._countTimer) { cancelAnimationFrame(self._countTimer); self._countTimer = null; }
      if (!animate) { shown = target; valueEl.textContent = fmt(target); return; }
      var from = shown, delta = target - from, t0 = null, dur = 850;
      function step(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        shown = Math.round(from + delta * eased);
        valueEl.textContent = fmt(shown);
        if (p < 1) { self._countTimer = requestAnimationFrame(step); } else { self._countTimer = null; shown = target; }
      }
      self._countTimer = requestAnimationFrame(step);
    }

    function render(state) {
      var total = state.total;
      var ratio = Math.max(0, Math.min(1, total / redline));

      if (fillEl._len) {
        fillEl.style.strokeDashoffset = fillEl._len * (1 - ratio);
      }
      setNeedle(ratio);
      countTo(total);

      var mode = 'idle';
      var caption = emptyMsg;

      if (state.count === 0) {
        mode = 'empty';
      } else if (state.unresolved > 0 && total === 0) {
        mode = 'unresolved';
        caption = unresolvedMsg;
      } else if (total >= redline) {
        mode = 'over';
        caption = redlineMsg;
      } else {
        mode = 'active';
        caption = activeMsg;
      }

      root.dataset.bbState = mode;
      if (capEl) capEl.textContent = caption;

      var label = mode === 'empty'
        ? emptyMsg + ' Redline ' + fmt(redline) + ' entries.'
        : fmt(total) + ' entries of ' + fmt(redline) + ' redline.';
      svg.setAttribute('aria-label', label);
      if (liveEl) liveEl.textContent = label;
    }

    // ---- cart plumbing ------------------------------------------------------
    function seed() {
      var lines = [];
      try { lines = JSON.parse(seedEl.textContent || '[]') || []; } catch (e) { lines = []; }
      for (var i = 0; i < lines.length; i++) {
        cache[lines[i].id] = { entries: lines[i].entries, multiplier: lines[i].multiplier };
      }
      return lines;
    }

    function normalize(cartJson) {
      var items = (cartJson && cartJson.items) || [];
      return items.map(function (it) {
        var c = cache[it.variant_id] || {};
        return {
          id: it.variant_id,
          qty: it.quantity,
          line_price: it.final_line_price !== undefined ? it.final_line_price : it.line_price,
          unit_price: it.final_price !== undefined ? it.final_price : it.price,
          entries: c.entries,
          multiplier: c.multiplier,
          properties: it.properties || {}
        };
      });
    }

    var inflight = false;
    function refresh(cartFromEvent) {
      // Horizon puts the new cart object on the event as detail.resource, so the
      // common path needs no network call at all. Only fall back to /cart.js when
      // an event did not carry one (first paint, or a non-theme cart write).
      if (cartFromEvent && cartFromEvent.items) {
        render(totalFor(normalize(cartFromEvent)));
        return;
      }
      if (inflight) return;
      inflight = true;
      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root + 'cart.js'
              : '/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (json) {
          if (json) render(totalFor(normalize(json)));
        })
        .catch(function () { /* leave last good reading on screen */ })
        .finally(function () { inflight = false; });
    }

    // Horizon's event contract, verified against assets/events.js in the theme
    // (ThemeEvents class). Do not guess these names — check that file after an
    // update, it is the single source of truth.
    //
    //   cart:update      CartAddEvent AND CartUpdateEvent both fire under this
    //                    name (CartAddEvent.eventName === ThemeEvents.cartUpdate)
    //   discount:update  a discount changed line prices. Entries derived from
    //                    price x multiplier move with it, so this MUST refresh
    //                    or a discounted cart reports a stale entry total.
    //   cart:error       a cart write failed; re-read so the gauge does not keep
    //                    showing a total for a change that did not land.
    //
    var EVENTS = ['cart:update', 'discount:update', 'cart:error'];
    this._cartEvents = EVENTS;
    // Bound handler kept on the instance so disconnectedCallback can remove it.
    this._onCartEvent = function (e) {
      var cart = e && e.detail && e.detail.resource;
      clearTimeout(self._debounce);
      self._debounce = setTimeout(function () { refresh(cart); }, 120);
    };
    EVENTS.forEach(function (name) {
      document.addEventListener(name, self._onCartEvent);
    });

    // Belt and braces: catch cart writes made by apps that bypass theme events.
    if (window.fetch) {
      var origFetch = window.fetch;
      self._origFetch = origFetch;
      self._patchedFetch = function () {
        var url = arguments[0];
        var href = typeof url === 'string' ? url : (url && url.url) || '';
        var p = origFetch.apply(this, arguments);
        if (/\/cart\/(add|change|update|clear)/.test(href)) {
          p.then(function () {
            clearTimeout(self._debounce);
            self._debounce = setTimeout(function () { refresh(); }, 120);
          }).catch(function () {});
        }
        return p;
      };
      window.fetch = self._patchedFetch;
    }

    drawStatic();
    render(totalFor(seed()));
  }

  disconnectedCallback() {
    // Remove the document-level cart listeners this instance added.
    if (this._onCartEvent && this._cartEvents) {
      var handler = this._onCartEvent;
      this._cartEvents.forEach(function (name) {
        document.removeEventListener(name, handler);
      });
    }
    // Cancel pending timers / rAF.
    if (this._debounce) { clearTimeout(this._debounce); this._debounce = null; }
    if (this._countTimer) { cancelAnimationFrame(this._countTimer); this._countTimer = null; }
    // Restore fetch only if ours is still the active patch, so we do not clobber
    // a patch installed by another instance layered on top of ours.
    if (this._patchedFetch && this._origFetch && window.fetch === this._patchedFetch) {
      window.fetch = this._origFetch;
    }
  }
}

if (!customElements.get('bb-entry-gauge')) customElements.define('bb-entry-gauge', BBEntryGauge);
